"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { History, X } from "lucide-react";
import productsminData from "@/public/ecomsaas-assets/cache/products.json";
import categoryData from "@/public/ecomsaas-assets/cache/category.json";
import { getAssetsBaseUrl } from "@/services/storeService";

// Define type for products based on your JSON structure
interface Product {
    id: number;
    name: string;
    eng_name?: string;
    imageurl: string;
    seo_url: string;
    product_tags?: string;
    sku?: string;
    category_id: number;
    visibility?: string;
}

// Define type for categories based on your JSON structure
interface Category {
    id: number;
    name: string;
    seo_url: string;
}

const SearchBar = ({
    isMobile,
    onNavigate,
}: {
    isMobile: boolean;
    onNavigate: (path: string) => void;
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [suggestions, setSuggestions] = useState<Product[]>([]);
    const [categorySuggestions, setCategorySuggestions] = useState<Category[]>([]);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const assets = `${getAssetsBaseUrl()}`

    const VIDEO_EXT = /\.(mp4|webm|mov|avi|wmv|mkv|flv|ogv)$/i;



    // Load recent searches from localStorage on mount
    useEffect(() => {
        const storedSearches = JSON.parse(localStorage.getItem("recentSearches") || "[]");
        setRecentSearches(storedSearches);
    }, []);


    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value.toLowerCase(); // Allow spaces
        setSearchQuery(query);

        if (query.length < 1) {
            setSuggestions([]);
            setCategorySuggestions([]);
            setShowSuggestions(query.length === 0 && recentSearches.length > 0);
            return;
        }


        const filteredProducts = (productsminData as any[])
            .filter((product) => product.visibility === 1) // Filter by visibility
            .filter((product) =>
                [product.name, product.eng_name, product.seo_url, product.product_tags, product.sku]
                    .some((field) => field?.toLowerCase().includes(query))
            )
            .map((product) => ({
                id: product.id,
                name: product.name,
                imageurl: product.imageurl,
                seo_url: product.seo_url,
                category_id: product.category_id,
            } as Product))
            .sort((a: Product, b: Product) => a.name.localeCompare(b.name))
            .slice(0, 5);

        // Filter categories based on their own fields and related products
        const filteredCategories = (categoryData as Category[])
            .filter((category) => {
                // Check category name and seo_url
                const matchesCategory = [category.name, category.seo_url].some(
                    (field) => field?.toLowerCase().includes(query)
                );
                // Check if any product in this category matches the query and has visibility === 1
                const matchesProduct = (productsminData as any[]).some(
                    (product) =>
                        product.category_id === category.id &&
                        product.visibility === 1 &&
                        [product.name, product.eng_name, product.seo_url, product.product_tags, product.sku].some(
                            (field) => field?.toLowerCase().includes(query)
                        )
                );
                return matchesCategory || matchesProduct;
            })
            .map((category) => ({
                id: category.id,
                name: category.name,
                seo_url: category.seo_url,
            }))
            .sort((a: Category, b: Category) => a.name.localeCompare(b.name))
            .slice(0, 5);

        setSuggestions(filteredProducts);
        setCategorySuggestions(filteredCategories);
        setShowSuggestions(true);
    };

    const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            // Store recent search in localStorage
            let recent = JSON.parse(localStorage.getItem("recentSearches") || "[]");
            if (!recent.includes(searchQuery)) {
                recent.unshift(searchQuery);
                recent = recent.slice(0, 5); // Keep only the latest 5 searches
                localStorage.setItem("recentSearches", JSON.stringify(recent));
                setRecentSearches(recent);
            }
            onNavigate(`/products?search=${encodeURIComponent(searchQuery)}`);
            setShowSuggestions(false);
        }
    };

    // Handle clicking a recent search
    const handleRecentSearchClick = (query: string) => {
        setSearchQuery(query);
        onNavigate(`/products?search=${encodeURIComponent(query)}`);
        setShowSuggestions(false);
    };

    // Handle clicking a product
    const handleProductClick = (product: Product) => {
        let recent = JSON.parse(localStorage.getItem("recentSearches") || "[]");
        if (!recent.includes(product.name)) {
            recent.unshift(product.name);
            recent = recent.slice(0, 5); // Keep only the latest 5 searches
            localStorage.setItem("recentSearches", JSON.stringify(recent));
            setRecentSearches(recent);
        }
        setShowSuggestions(false);
        onNavigate(`/p/${product.seo_url}`);
    };

    // Clear all recent searches
    const handleClearAll = () => {
        localStorage.setItem("recentSearches", JSON.stringify([]));
        setRecentSearches([]);
        setShowSuggestions(false);
    };

    // Remove a single recent search
    const handleRemoveSearch = (query: string) => {
        const updatedSearches = recentSearches.filter((search) => search !== query);
        localStorage.setItem("recentSearches", JSON.stringify(updatedSearches));
        setRecentSearches(updatedSearches);
        if (updatedSearches.length === 0) {
            setShowSuggestions(false);
        }
    };

    // Close suggestions on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getFirstImageUrl = (imageurl: string | string[]): string | null => {
        // 1. Normalise to an array
        const urls = Array.isArray(imageurl)
            ? imageurl
            : imageurl
                ?.split(",")
                .map((s) => s.trim())
                .filter(Boolean) ?? [];

        // 2. Find the first URL that is NOT a video
        const firstImg = urls.find((u) => !VIDEO_EXT.test(u));
        return firstImg ?? null;               // null → “no usable image”
    };

    // Truncate text to 40 characters
    const truncateText = (text: string, maxLength: number = 25): string => {
        if (text.length <= maxLength) return text;
        return text.slice(0, maxLength) + "...";
    };

    // Calculate dropdown width to match input width
    const dropdownWidth = inputRef.current ? `${inputRef.current.offsetWidth}px` : "100%";

    return (
        <div
            ref={searchRef}
            className={isMobile ? "my-4 pb-3 px-4 flex items-center" : "my-3 flex w-full items-center justify-center"}
        >
            <form className="relative w-full rounded-md md:w-[400px] lg:w-[700px] mx-auto" onSubmit={handleSearchSubmit}>
                <input
                    ref={inputRef}
                    type="search"
                    placeholder="Search Products..."
                    className={`Header-input w-full border-[1px] border-gray-300  rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-primary focus:rounded-md`}
                    aria-label="Search products"
                    id={isMobile ? "searchInputMobile" : "searchInput"}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => setShowSuggestions(recentSearches.length > 0 || searchQuery.trim().length >= 2)}
                />
                {/* <button
                    type="submit" aria-label="search button"
                    className="Header-btn absolute right-0 top-0 h-full px-4 bg-primary rounded-r-md"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.3-4.3" />
                    </svg>
                </button> */}

                {showSuggestions && (
                    <div
                        className="absolute z-50 bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-80 overflow-y-auto"
                        style={{ width: dropdownWidth }}
                    >
                        {searchQuery.length === 0 && recentSearches.length > 0 ? (
                            <>
                                <div className="flex justify-between items-center px-4 py-2">
                                    <p className="text-sm text-gray-700">Recent Searches</p>
                                    <button
                                        onClick={handleClearAll}
                                        className="text-sm text-red-500 hover:text-red-700"
                                    >
                                        Clear All
                                    </button>
                                </div>
                                <div className="flex items-center gap-2 p-2 justify-start flex-wrap">
                                    {recentSearches.map((search, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between rounded-full p-2 bg-blue-600/20 hover:bg-gray-100"
                                        >
                                            <Link
                                                href={`/products?search=${encodeURIComponent(search)}`}
                                                className="flex whitespace-nowrap items-center gap-2 text-[11px] md:text-sm text-gray-800"
                                                onClick={() => handleRecentSearchClick(search)}
                                            >
                                                <History className="h-4 w-4" /> {truncateText(search)}
                                            </Link>
                                            <button
                                                onClick={() => handleRemoveSearch(search)}
                                                className="ml-2 text-gray-500 hover:text-red-500"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <>
                                {suggestions.length > 0 && (
                                    <>
                                        <p className="text-sm text-gray-700 px-4 py-2">Products</p>

                                        {suggestions.map((product) => {
                                            const firstImageUrl = getFirstImageUrl(product.imageurl);
                                            const imgSrc = firstImageUrl ? `${assets}${firstImageUrl}` : null;

                                            return (
                                                <Link
                                                    key={product.id}
                                                    href={`/p/${product.seo_url}`}
                                                    className="flex items-center p-3 hover:bg-gray-100"
                                                    onClick={() => handleProductClick(product)}
                                                >
                                                    <LazyLoadImage
                                                        src={imgSrc ?? "/ecomsaas-assets/images/no-image.webp"}
                                                        placeholderSrc="/ecomsaas-assets/images/no-image.webp"
                                                        alt={product.name}
                                                        width={40}
                                                        height={40}
                                                        className="object-cover rounded-md mr-3"
                                                        onError={(e) => {
                                                            e.currentTarget.src = "/ecomsaas-assets/images/no-image.webp";
                                                        }}
                                                    />
                                                    <span className="text-sm text-gray-800">
                                                        {truncateText(product.name)}
                                                    </span>
                                                </Link>
                                            );
                                        })}
                                    </>
                                )}

                                {categorySuggestions.length > 0 ? (
                                    <>
                                        <p className="text-sm text-gray-700 px-4 py-2">Categories</p>
                                        <div className="flex items-center flex-wrap gap-2 p-2">
                                            {categorySuggestions.map((category) => (
                                                <Link
                                                    key={category.id}
                                                    href={`/products/${category.seo_url}`}
                                                    className="flex items-center whitespace-nowrap rounded-full bg-primary/20 p-2 px-3 hover:bg-gray-100"
                                                    onClick={() => {
                                                        setShowSuggestions(false);
                                                        onNavigate(`/products/${category.seo_url}`);
                                                    }}
                                                >
                                                    <span className="text-sm text-gray-800">{truncateText(category.name)}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    suggestions.length === 0 && (
                                        <></>
                                    )
                                )}

                                {suggestions.length === 0 && categorySuggestions.length === 0 && (
                                    <Link
                                        href="/products"
                                        className="block text-sm text-gray-700 px-4 py-2 hover:bg-gray-100"
                                        onClick={() => {
                                            setShowSuggestions(false);
                                            onNavigate('/products');
                                        }}
                                    >
                                        No results found. <span className="text-blue-800 underline"> Browse all </span> products?
                                    </Link>
                                )}
                            </>
                        )}
                    </div>
                )}
            </form>
        </div>
    );
};

export default SearchBar;